<template>
    <Layout>
        <template #form>
            <Form
                @submit="onSubmit"
                :keep-me-signed-in.sync="keepMeSignedIn"
                :show-feedback="showFeedback"
                show-options
            >
                <template #title>
                    {{ $t('title') }}
                </template>
                <template #subtitle>
                    {{ $t('subtitle') }}
                </template>
                <template #feedback>
                    <feedback-auth :email="showFeedbackEmail" :cpf="showFeedbackCpf" />
                </template>
                <template #fields>
                    <InputUsername
                        v-model="username"
                        :state="validateField('username')"
                        :invalid-feedback="usernameInvalidFeedback"
                        autofocus
                    />
                    <InputPassword
                        v-model="password"
                        :state="validateField('password')"
                        :invalid-feedback="passwordInvalidFeedback"
                    />
                </template>
                <template #button>
                    <BtnSubmit sign-in :loading="loading" />
                </template>
            </Form>
        </template>
    </Layout>
</template>

<script>
import api from '@/services/api'
import oauth from '@/services/oauth-api'
import AuthService from '@/services/auth'
import { validationMixin } from '@/mixins'
import { required, email, or } from 'vuelidate/lib/validators'
import { cpf } from '@/lib/validators'
import { onlyNumbers } from '@/lib/utils'
import { getSubdomain, redirect } from '@/lib/url'
import { Layout, Form, InputUsername, InputPassword, BtnSubmit, FeedbackAuth } from './components'
import {
    URL_COGNITO_CONDOR,
    URL_COGNITO_SEBRAESC,
    URL_COGNITO_BASF,
    URL_COGNITO_HAOC,
    URL_COGNITO_APSEN,
    URL_COGNITO_SND,
} from './consts/cognito'

export default {
    name: 'Login',
    mixins: [validationMixin],
    components: {
        Layout,
        Form,
        InputUsername,
        InputPassword,
        BtnSubmit,
        FeedbackAuth,
    },
    beforeRouteEnter(to, from, next) {
        const client = getSubdomain()
        if (client === 'haoc') return redirect(URL_COGNITO_HAOC)
        if (client === 'condor') return redirect(URL_COGNITO_CONDOR)
        if (client === 'okr-sebraesc') return redirect(URL_COGNITO_SEBRAESC)
        if (client === 'sebraesc') return redirect(URL_COGNITO_SEBRAESC)
        if (client === 'basf') return redirect(URL_COGNITO_BASF)
        if (client === 'apsen') return redirect(URL_COGNITO_APSEN)
        if (client === 'snd') return redirect(URL_COGNITO_SND)
        return next()
    },
    data() {
        return {
            username: '',
            password: '',
            keepMeSignedIn: false,
            showFeedbackEmail: false,
            showFeedbackCpf: false,
            loading: false,
        }
    },
    validations: {
        username: {
            required,
            or: or(email, cpf),
        },
        password: {
            required,
        },
        keepMeSignedIn: {
            required,
        },
    },
    computed: {
        showFeedback() {
            return this.showFeedbackEmail || this.showFeedbackCpf
        },
        usernameInvalidFeedback() {
            if (!this.$v.username.required) return this.$t('required')
            if (!this.$v.username.or) return this.$t('emailOrCpf')
        },
        passwordInvalidFeedback() {
            if (!this.$v.password.required) return this.$t('required')
        },
    },
    methods: {
        onSubmit() {
            if (this.validate()) {
                this.loading = true
                this.showFeedbackEmail = false
                this.showFeedbackCpf = false
                if (email(this.username)) this.goWithEmail()
                if (cpf(this.username)) this.goWithCpf()
            }
        },
        async goWithEmail() {
            try {
                await AuthService(api, oauth).login(this.username, this.password, this.keepMeSignedIn)
            } catch (e) {
                this.showFeedbackEmail = true
            } finally {
                this.loading = false
            }
        },
        async goWithCpf() {
            try {
                const cpf = onlyNumbers(this.username)
                await AuthService(api, oauth).login(cpf, this.password, this.keepMeSignedIn, true)
            } catch (e) {
                this.showFeedbackCpf = true
            } finally {
                this.loading = false
            }
        },
    },
    i18n: {
        messages: {
            'en-US': {
                title: 'Welcome!',
                subtitle: 'Sign in to access the platform',
                required: 'Required field',
                emailOrCpf: 'Invalid email address',
            },
            'es-ES': {
                title: '¡Bienvenido!',
                subtitle: 'Inicia sesión para acceder a la plataforma',
                required: 'Campo obligatorio',
                emailOrCpf: 'Dirección de correo electrónico inválida',
            },
            'pt-BR': {
                title: 'Boas-vindas!',
                subtitle: 'Faça login para acessar a plataforma',
                required: 'Preenchimento obrigatório',
                emailOrCpf: 'Informe um email ou CPF válido',
            },
        },
    },
}
</script>
